import { api } from './client';
import { API_BASE_URL } from '../config/api';
import { readStoredAuth } from '../pages/Account/profileFormUtils';
import {
  computeSubscriptionPeriodCharges,
  countDeliveryWindowsForSlots,
  getSubscriptionFreeDeliveryRadiusKm,
} from '../pages/MealPlans/subscriptionChargesUtils';
import { distanceKmFromKitchen } from '../pages/Cart/cartDistanceUtils';
import { REGION_DISPLAY_LABELS, buildSlotPatternRows } from '../utils/subscriptionDisplay';
import { isOurMenuOnlyProductList } from '../utils/subscriptionCartItems';

/** Feature flag: use digirasa-api meal-plan subscriptions instead of Node SQLite. */
export const USE_API_MEAL_PLANS =
  String(process.env.REACT_APP_USE_API_MEAL_PLANS || 'true').toLowerCase() !== 'false';

function authHeaders() {
  const { token } = readStoredAuth();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

const base = (path) => `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

export async function fetchMealPlanCatalog() {
  const settled = await Promise.allSettled([
    api.get(base('/meal-plans/specialties'), { timeoutMs: 25000 }),
    api.get(base('/meal-plans/slots'), { timeoutMs: 25000 }),
    api.get(base('/meal-plans/prices'), { timeoutMs: 25000 }),
  ]);

  const [specialtiesRes, slotsRes, pricesRes] = settled;
  if (specialtiesRes.status === 'rejected') {
    throw specialtiesRes.reason;
  }

  return {
    specialties: specialtiesRes.value?.data?.specialties || [],
    slots: slotsRes.status === 'fulfilled' ? (slotsRes.value?.data?.slots || []) : [],
    prices: pricesRes.status === 'fulfilled' ? (pricesRes.value?.data?.prices || []) : [],
  };
}

export async function fetchIndicativeMenu({ specialtyCode, regionKey } = {}) {
  const params = new URLSearchParams();
  if (specialtyCode) params.set('specialtyCode', specialtyCode);
  if (regionKey) params.set('regionKey', regionKey);
  const q = params.toString() ? `?${params}` : '';
  const { data } = await api.get(base(`/meal-plans/indicative-menu${q}`));
  return data?.items || [];
}

export async function fetchDayMenu({ fromDate, toDate, specialtyCode, regionKey } = {}) {
  const params = new URLSearchParams({ fromDate, toDate });
  if (specialtyCode) params.set('specialtyCode', specialtyCode);
  if (regionKey) params.set('regionKey', regionKey);
  const { data } = await api.get(base(`/meal-plans/day-menu?${params}`));
  return data;
}

/** Active day-count discount tiers (admin Meal Plan Masters → Day discounts). */
export async function fetchDayDiscounts() {
  const { data } = await api.get(base('/meal-plans/day-discounts'));
  return data?.discounts || [];
}

/**
 * Full specialty catalog for CREATE YOUR PLAN / View details:
 * specialties + slots + prices + indicative menus (all regions).
 */
export async function fetchSpecialtyCatalogPlans(overlaysByCode = {}) {
  // Lazy import keeps catalog mapping out of the hot path for unrelated API calls.
  const {
    SPECIALTY_UI_OVERLAYS,
    buildPlanFromApi,
  } = await import('../data/specialtyCatalogApi');
  const { resolveSpecialtyIcon } = await import('../data/specialtyIcons');

  const { specialties, slots, prices } = await fetchMealPlanCatalog();
  const active = (specialties || [])
    .filter((s) => s.status !== 'hidden')
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const plans = await Promise.all(
    active.map(async (specialty) => {
      const indicativeItems = await fetchIndicativeMenu({ specialtyCode: specialty.code });
      const baseOverlay = SPECIALTY_UI_OVERLAYS[specialty.code] || {};
      const iconCode = specialty.iconCode || baseOverlay.iconCode || specialty.code;
      const overlay = {
        ...baseOverlay,
        ...(overlaysByCode[specialty.code] || {}),
        tagline: specialty.caption || baseOverlay.tagline || '',
        iconCode,
        logoUrl: specialty.logoUrl || baseOverlay.logoUrl || null,
        iconComponent: resolveSpecialtyIcon(iconCode, specialty.code),
      };
      return buildPlanFromApi({
        specialty,
        slots,
        prices,
        indicativeItems,
        overlay,
      });
    }),
  );

  return {
    specialties: specialties || [],
    slots: slots || [],
    prices: prices || [],
    plans: plans.filter(Boolean),
  };
}

/**
 * Pick the highest minDays tier that the subscription still qualifies for.
 * @returns {{ discountPercent: number, minDays: number|null, label: string|null }}
 */
export function resolveDayDiscountTier(discounts, deliveryDays) {
  const days = Number(deliveryDays) || 0;
  if (!Array.isArray(discounts) || days < 1) {
    return { discountPercent: 0, minDays: null, label: null };
  }
  let best = null;
  discounts.forEach((row) => {
    const minDays = Number(row.minDays);
    const discountPercent = Number(row.discountPercent);
    if (!Number.isFinite(minDays) || minDays < 1) return;
    if (!Number.isFinite(discountPercent) || discountPercent < 0) return;
    if (minDays > days) return;
    if (
      !best
      || minDays > best.minDays
      || (minDays === best.minDays && discountPercent > best.discountPercent)
    ) {
      best = {
        discountPercent,
        minDays,
        label: row.label || null,
      };
    }
  });
  return best || { discountPercent: 0, minDays: null, label: null };
}

export function dayDiscountFactorFromPercent(discountPercent) {
  const pct = Math.min(100, Math.max(0, Number(discountPercent) || 0));
  return 1 - pct / 100;
}

export async function createWebMealPlanSubscription(body) {
  const { token } = readStoredAuth();
  if (!token) {
    const err = new Error('Please sign in to create a meal plan subscription.');
    err.code = 'AUTH_REQUIRED';
    throw err;
  }
  const { data } = await api.post(base('/meal-plans/subscriptions'), body, {
    headers: authHeaders(),
  });
  return data;
}

export async function listMyWebSubscriptions() {
  const { data } = await api.get(base('/meal-plans/subscriptions/mine'), {
    headers: authHeaders(),
  });
  return data;
}

export async function getSubscriptionPaymentLink(subscriptionId) {
  const { data } = await api.get(
    base(`/meal-plans/subscriptions/${subscriptionId}/payment-link`),
    { headers: authHeaders() },
  );
  return data;
}

export async function createBulkEnquiry(body) {
  const headers = authHeaders();
  const { data } = await api.post(base('/meal-plans/bulk-enquiry'), body, {
    ...(Object.keys(headers).length ? { headers } : {}),
  });
  return data;
}

export async function listMyBulkEnquiries() {
  const { data } = await api.get(base('/meal-plans/bulk-enquiry/mine'), {
    headers: authHeaders(),
  });
  return data;
}

export async function trackBulkEnquiry(lookupCode) {
  const { data } = await api.get(
    base(`/meal-plans/bulk-enquiry/${encodeURIComponent(lookupCode)}`),
  );
  return data;
}

/**
 * Resolve full-period charges from create payload (or precomputed charges).
 * @param {object} payload
 */
export function resolvePayloadCharges(payload) {
  if (payload?.charges && Number.isFinite(Number(payload.charges.totalPrice))) {
    return payload.charges;
  }
  const customer = payload?.customer || {};
  const days = Number(payload?.deliveryDayCount || payload?.daysCount || payload?.count || 0)
    || Math.max(
      1,
      Math.round(
        (new Date(payload?.endDate) - new Date(payload?.startDate)) / 86400000,
      ) + 1,
    );
  const slotCount = Math.max(1, (payload?.mealSlots || []).length);
  const tripsPerDay = Math.max(
    1,
    countDeliveryWindowsForSlots(payload?.mealSlots || []),
  );
  const deliveryTripCount = Math.max(
    1,
    Number(payload?.deliveryTripCount)
      || Number(payload?.deliverySlotCount)
      || days * tripsPerDay,
  );
  const subtotal = Number(
    payload?.estimatedTotal ?? payload?.subtotal ?? payload?.total ?? payload?.grandTotal ?? 0,
  );
  const freeDeliveryRadiusKm = getSubscriptionFreeDeliveryRadiusKm({
    planId: payload?.planId,
    deliveryDays: days,
  });
  let distanceKm = Number.isFinite(Number(payload?.distanceKm))
    ? Number(payload.distanceKm)
    : null;
  if (distanceKm == null) {
    distanceKm = distanceKmFromKitchen({
      latitude: customer.latitude,
      longitude: customer.longitude,
    });
  }

  return computeSubscriptionPeriodCharges({
    periodSubtotal: subtotal,
    days,
    deliveryTripCount,
    distanceKm,
    freeDeliveryRadiusKm,
    allowFallbackDelivery: true,
  });
}

export async function pauseWebSubscription(subscriptionId) {
  const { data } = await api.post(
    base(`/meal-plans/subscriptions/${subscriptionId}/pause`),
    {},
    { headers: authHeaders() },
  );
  return data;
}

export async function resumeWebSubscription(subscriptionId) {
  const { data } = await api.post(
    base(`/meal-plans/subscriptions/${subscriptionId}/resume`),
    {},
    { headers: authHeaders() },
  );
  return data;
}

export async function cancelWebSubscription(subscriptionId) {
  const { data } = await api.post(
    base(`/meal-plans/subscriptions/${subscriptionId}/cancel`),
    {},
    { headers: authHeaders() },
  );
  return data;
}

export async function listSubscriptionHolds(subscriptionId) {
  const { data } = await api.get(
    base(`/meal-plans/subscriptions/${subscriptionId}/holds`),
    { headers: authHeaders() },
  );
  return data;
}

/** @param {{ items: Array<{ date: string, slotCodes: string[] }>, mode?: 'auto'|'open'|'date', resumeDate?: string }} body */
export async function pauseSubscriptionSlots(subscriptionId, body) {
  const { data } = await api.post(
    base(`/meal-plans/subscriptions/${subscriptionId}/holds/pause`),
    body,
    { headers: authHeaders() },
  );
  return data;
}

/** @param {{ items: Array<{ date: string, slotCodes: string[] }> }} body */
export async function cancelSubscriptionSlots(subscriptionId, body) {
  const { data } = await api.post(
    base(`/meal-plans/subscriptions/${subscriptionId}/holds/cancel`),
    body,
    { headers: authHeaders() },
  );
  return data;
}

/** @param {{ holdIds: number[], resumeDate: string }} body */
export async function resumeSubscriptionSlots(subscriptionId, body) {
  const { data } = await api.post(
    base(`/meal-plans/subscriptions/${subscriptionId}/holds/resume`),
    body,
    { headers: authHeaders() },
  );
  return data;
}

export function buildWebSubscriptionBodyFromPayload(payload) {
  const customer = payload.customer || {};
  const slotCodes = (payload.mealSlots || []).map(String);
  // Specialty code/name come from catalog selection / API — do not invent labels here.
  const specialtyCode = payload.specialtyPlanId
    || payload.specialtyCode
    || (isOurMenuOnlyProductList(payload.products) ? 'general' : null);
  if (!specialtyCode) {
    throw new Error('A specialty plan is required to create a subscription.');
  }
  const specialtyName = payload.specialtyName || undefined;
  const supportsRegion = payload.specialtySupportsRegion === true;
  const regionKey = supportsRegion
    ? (payload.specialtyRegionKey || payload.regionKey || null)
    : null;
  const regionLabel = supportsRegion
    ? (payload.regionLabel
      || payload.specialtyRegionLabel
      || (regionKey ? (REGION_DISPLAY_LABELS[String(regionKey).toLowerCase()] || null) : null))
    : null;
  const start = payload.startDate;
  const end = payload.endDate;
  const days = Number(payload.deliveryDayCount || payload.daysCount || payload.count || 0)
    || Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1);
  const slotCount = Math.max(1, slotCodes.length);
  const tripsPerDay = Math.max(1, countDeliveryWindowsForSlots(slotCodes));
  const deliveryTripCount = Math.max(
    1,
    Number(payload.deliveryTripCount)
      || Number(payload.deliverySlotCount)
      || days * tripsPerDay,
  );
  const charges = resolvePayloadCharges(payload);
  const {
    subtotal,
    totalTax,
    totalDeliveryCharge,
    totalPackagingCharge,
    totalPrice,
    perDayPrice,
    pricePerSlot,
    deliveryPerTrip,
    packagingPerDay,
    gstPerDay,
    gstPercent,
    distanceKm,
    isFreeDeliveryPlan,
    freeDeliveryRadiusKm,
    usedFallbackDelivery,
  } = charges;

  const snapshotPatternRows = buildSlotPatternRows({
    ...payload,
    startDate: start,
    endDate: end,
    fromDate: start,
    toDate: end,
  });

  return {
    specialtyCode,
    slotCodes,
    isOrganic: Boolean(payload.specialtyOrganic),
    regionKey,
    fromDate: start,
    toDate: end,
    // Web subscriptions bank one unit for each scheduled delivery trip window.
    count: deliveryTripCount,
    orderDate: new Date().toISOString().slice(0, 10),
    totalPrice,
    subtotal,
    totalTax,
    totalDeliveryCharge,
    totalPackagingCharge,
    address: {
      address: [customer.address, customer.locationArea].filter(Boolean).join(', '),
      city: customer.city || 'Hyderabad',
      state: customer.state || 'Telangana',
      country: customer.country || 'India',
      postalCode: customer.pincode || '',
      latitude: Number.isFinite(Number(customer.latitude)) ? Number(customer.latitude) : 0,
      longitude: Number.isFinite(Number(customer.longitude)) ? Number(customer.longitude) : 0,
    },
    userInfo: {
      mobileNumber: String(customer.phone || '').replace(/\D/g, ''),
      name: `${customer.firstName || ''} ${customer.lastName || ''}`.trim(),
      emailID: customer.email || '',
    },
    snapshotExtra: {
      title: payload.title,
      planId: payload.planId,
      planLabel: payload.planLabel,
      mealLabel: payload.mealLabel,
      mealSlots: slotCodes,
      scheduleId: payload.scheduleId,
      scheduleLabel: payload.scheduleLabel,
      days: payload.days,
      startDate: start,
      endDate: end,
      slotPatternRows: snapshotPatternRows,
      webPayloadHint: true,
      deliveryBank: "trip",
      deliveryDayCount: days,
      deliverySlotCount: Number(payload.deliverySlotCount) || days * slotCount,
      deliveryTripCount,
      slotCount,
      perDayPrice,
      pricePerSlot,
      estimatedPerDelivery: Number(payload.estimatedPerDelivery) || 0,
      estimatedTotal: totalPrice,
      totalPrice,
      subtotal,
      totalTax,
      totalDeliveryCharge,
      totalPackagingCharge,
      deliveryPerTrip,
      packagingPerDay,
      gstPerDay,
      gstPercent,
      distanceKm,
      isFreeDeliveryPlan,
      freeDeliveryRadiusKm,
      usedFallbackDelivery: Boolean(usedFallbackDelivery),
      charges,
      pricing: payload.pricing && typeof payload.pricing === 'object' ? payload.pricing : undefined,
      customer: {
        firstName: customer.firstName || '',
        lastName: customer.lastName || '',
        phone: customer.phone || '',
        email: customer.email || '',
        locationArea: customer.locationArea || '',
        address: customer.address || '',
        city: customer.city || '',
        country: customer.country || '',
        pincode: customer.pincode || '',
        state: customer.state || '',
      },
      products: Array.isArray(payload.products) ? payload.products : [],
      itemSchedules: Array.isArray(payload.itemSchedules) ? payload.itemSchedules : [],
      specialtyPlanId: specialtyCode,
      specialtyName: specialtyName || undefined,
      specialtyOrganic: Boolean(payload.specialtyOrganic),
      specialtySupportsRegion: supportsRegion,
      specialtyRegionKey: regionKey,
      regionKey,
      regionLabel: regionLabel || undefined,
      dayBandId: payload.dayBandId || undefined,
      dayBandMinDays: payload.dayBandMinDays != null ? Number(payload.dayBandMinDays) : undefined,
      planCalendarDays: payload.planCalendarDays != null ? Number(payload.planCalendarDays) : undefined,
      uiSlotCodes: Array.isArray(payload.uiSlotCodes) ? payload.uiSlotCodes.map(String) : undefined,
      slotLabels: Array.isArray(payload.uiSlotLabels)
        ? payload.uiSlotLabels.map(String)
        : (Array.isArray(payload.uiSlotCodes)
          ? payload.uiSlotCodes.map((code) => {
            if (String(code) === 'full_day') return 'Full Day';
            const pretty = String(code).replace(/_/g, ' ');
            return pretty.charAt(0).toUpperCase() + pretty.slice(1);
          })
          : undefined),
      selectionMode: payload.selectionMode || undefined,
    },
  };
}
